import { Checkbox, Form, Select, Spin, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BoxContainer from 'base/ui/components/BoxContainer';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomProTable from 'base/ui/components/CustomTable';
import CustomSelect from 'base/ui/components/CustomSelect';
import { ProColumns } from '@ant-design/pro-components';
import { SYSTEM_TITLE } from 'utils/constants';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import { commonMasterDataPresenter, commonMasterDataStore } from 'stores/root_store';
import { AccessModel, IAccessServices, PageAccess } from 'services/RawData/access_services';
import { usePermission } from 'hooks/usePermission';
import { getRole } from 'utils/localStorageHelper';
import CustomButtonIcon from 'base/ui/components/Button/buttonIcon';
import CustomInput from 'base/ui/components/CustomInput';
import { SaveFilled } from '@ant-design/icons';

const accessWebPage = (accessServices: IAccessServices) => {
  return observer(() => {
    const pagetitle = 'Phân quyền chức năng web';
    document.title = pagetitle + ' - ' + SYSTEM_TITLE;
    usePermission('INDEX'); // Kiểm tra xem được quyền truy cập vào trang
    const [form] = Form.useForm();
    const role = getRole()
    const { t } = useTranslation();

    // ========================USE STATE================================================================
    // Loading all page
    const [loading, setLoading] = useState(false);

    // Draft cache: gõ không re-render
    const paramsDraftRef = useRef<Record<string, string>>({});

    // watch role cho key (đổi role sẽ remount)
    const roleId = Form.useWatch('role', form);

    const [dataVersion, setDataVersion] = useState(0);

    const handleUpdateParams = async (rolesId: string, pageId: string, params: string) => {
      try {
        const result = await accessServices.updateParamsInPage({
          rolesId,
          pageId,
          params,
        });

        if (result.isSuccess) {
          // update cache
          paramsDraftRef.current[`${pageId}`] = params;
          message.success('Lưu thành công');

          // reload
          getAccessFromRole(form.getFieldValue('role'));
        } else {
          message.error(result.message);
        }
      } catch (error) { }
    };


    // Columns table title
    const defaultColumn: ProColumns<PageAccess>[] = useMemo(
      () => [
        {
          title: 'STT',
          dataIndex: 'index',
          valueType: 'index',
          key: 'stt',
          width: 40,
          align: 'center',
          // render: (text, record) => <div className="!text-center">{record.stt}</div>,
        },
        // Chức năng
        {
          title: 'Chức năng',
          dataIndex: 'pageName',
          key: 'pageName',
          width: 200,
          render: (text, record) => (
            <Checkbox
              checked={record.isChooseAll}
              value={record.pageId}
              onChange={(e) => {
                handleCheckPage(record.pageId, e.target.checked)
              }}
            >
              {text}
            </Checkbox>
          ),
        },
        // Thao tác
        {
          title: 'Thao tác',
          dataIndex: 'thaotac',
          key: 'thaotac',
          width: 200,
          render: (_, record) => (
            <div className="flex items-center gap-4">
              {record.functionAccessResponses.map((item, idx) => {
                return (
                  <Checkbox
                    key={item.functionId}
                    checked={item.selected}
                    value={item.functionId}
                    onChange={(e) => {
                      handleCheckFunction(item.functionId, record.pageId, e.target.checked)
                    }}
                  >
                    {item.functionName}
                  </Checkbox>
                );
              })}
            </div>
          ),
        },

        {
          title: 'Tham số',
          dataIndex: 'params',
          key: 'params',
          width: 250,
          render: (_, record) => {
            const pageKey = `${record.pageId}`;
            const initVal = paramsDraftRef.current[pageKey] ?? record.params ?? '';

            return (
              <div className="flex items-center gap-x-2">
                <div className="flex-1 min-w-0">
                  <CustomInput
                    // remount khi fetch xong (dataVersion) hoặc đổi role
                    key={`${roleId ?? 'no-role'}-${dataVersion}-${record.pageId}`}
                    defaultValue={initVal}
                    className="w-full"
                    onChange={(e: any) => {
                      const v = e?.target?.value ?? e ?? '';
                      paramsDraftRef.current[pageKey] = v;
                    }}
                    onBlur={(e: any) => {
                      const v = e?.target?.value ?? e ?? '';
                      paramsDraftRef.current[pageKey] = v;
                    }}
                  />
                </div>

                <CustomButtonIcon
                  color="#008000"
                  className="p-[5px] shrink-0"
                  onClick={() => {
                    const currentRole = form.getFieldValue('role');
                    const val = paramsDraftRef.current[pageKey] ?? '';
                    handleUpdateParams(currentRole, record.pageId, val);
                  }}
                >
                  <SaveFilled className="!text-[20px] !text-[#008000]" />
                </CustomButtonIcon>
              </div>
            );
          },
        },
      ],
      [t, roleId, dataVersion],
    );

    // Custom default columns table with resizeable
    const [columns, setColumn] = useState<ProColumns<PageAccess>[]>(defaultColumn);
    const [dataAccess, setDataAccess] = useState<AccessModel[]>([])

    // Sync columns với defaultColumn khi role hoặc dataVersion thay đổi
    useEffect(() => {
      setColumn(defaultColumn);
    }, [defaultColumn]);

    // Clear drafts khi role thay đổi
    useEffect(() => {
      paramsDraftRef.current = {};
    }, [roleId]);

    // ========================FUNCTION ================================================================
    const handleResize =
      (index: number) =>
        (e: any, { size }: any) => {
          // Set columns
          setColumn((prevColumns) => {
            const nextColumns = [...prevColumns];
            nextColumns[index] = {
              ...nextColumns[index],
              width: size.width,
            };
            return nextColumns;
          });
        };
    const getListRoles = async () => {
      if (!commonMasterDataStore.listRoles.length) {
        await commonMasterDataPresenter.getDropdownRole(commonMasterDataStore)
      }
      form.setFieldValue('role', commonMasterDataStore.listRoles[0].key)
      getAccessFromRole(commonMasterDataStore.listRoles[0].key)
    }

    const getAccessFromRole = async (roleId: string) => {
      try {
        setLoading(true);
        const result = await accessServices.getAccessByRoleId(roleId)
        setLoading(false);
        if (result.isSuccess) {
          setDataAccess(result.data.filter(access => role !== 'SYSADMIN' ? (access.pageAccessResponses.length && access.menuName !== "Cài đặt" && access.menuName !== "Cấu hình") : access.pageAccessResponses.length))
          // Bump version để force remount CustomInput với data mới
          setDataVersion(v => v + 1);
        } else {
          message.error(result.message)
        }
      } catch (error) {
        console.log(error);
      }
    }

    const handleCheckMenu = async (menuId: string, checked: boolean) => {
      try {
        const result = await accessServices.updateFunctionPermissionInMenu({ check: checked, menuId: menuId, roleId: form.getFieldValue('role') })
        if (result.isSuccess) {
          getAccessFromRole(form.getFieldValue('role'))
        }
      } catch (error) {
        console.log(error);
      }
    }
    const handleCheckPage = async (pageId: string, checked: boolean) => {
      try {
        const result = await accessServices.updateFunctionPermissionInPage({ check: checked, pageId: pageId, roleId: form.getFieldValue('role') })
        if (result.isSuccess) {
          getAccessFromRole(form.getFieldValue('role'))
        }
      } catch (error) {
        console.log(error);
      }
    }
    const handleCheckFunction = async (functionId: string, pageId: string, checked: boolean) => {
      try {
        const result = await accessServices.updateFunctionPermissions({ check: checked, functionId: functionId, roleId: form.getFieldValue('role'), pageId: pageId })
        if (result.isSuccess) {
          getAccessFromRole(form.getFieldValue('role'))
        }
      } catch (error) {
        console.log(error);
      }
    }
    // scroll to top
    useEffect(() => {
      window.scrollTo(0, 0);
      getListRoles()
    }, []);


    return (
      <div className="w-full">
        {loading && <div className='fixed w-screen h-screen bg-black/20 top-0 z-50 flex justify-center items-center'> <Spin></Spin></div>}
        {/* ==================================== Breadcrumb ========================================= */}
        <CustomBreadcrumb pagetitle={[{ title: pagetitle, link: '/MasterData/Access' }]} />

        {/* Tables */}
        <BoxContainer>
          <Form colon={false} form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 14 }}  >
            <Form.Item name={'role'} label={<h3 className='font-medium'>Nhóm người dùng</h3>}>
              <Select onChange={(value, option) => {
                getAccessFromRole(value)
              }} options={commonMasterDataStore.listRoles} fieldNames={{ label: 'value', value: 'key' }} />
            </Form.Item>
          </Form>
          {dataAccess.map((item, index) => {
            return (
              <div key={item.menuId} className='border border-primary/90 mb-4 rounded-t-[4px]'>
                <div className='w-full bg-primary/90 px-3 py-2 rounded-t-sm'>
                  <Checkbox
                    className='!text-white'
                    value={item.menuId}
                    checked={item.isChooseAll}
                    onChange={(e) => {
                      handleCheckMenu(item.menuId, e.target.checked)

                    }}
                  >
                    {item.menuName}
                  </Checkbox>
                </div>
                <CustomProTable<PageAccess>
                  className='w-full'
                  columns={columns}
                  handleResize={handleResize}
                  rowKey="pageId"
                  size="small"
                  bordered
                  scroll={{ x: undefined }}
                  search={false}
                  options={false}
                  // loading={loading}
                  dataSource={item.pageAccessResponses}
                  pagination={false}

                />
              </div>
            );
          })}

        </BoxContainer>
      </div>
    );
  });
};

export default accessWebPage;
